"use client";

import React, { useState, useEffect } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { Button } from "../components/ui/button";
import { LimitedInput } from "../components/ui/limited-input";
import { Label } from "../components/ui/label";
import { useToast } from "../components/ui/use-toast";
import api, { persistAuthSession } from "../services/api";
import { FIELD_LIMITS } from "../utils/fieldLimits";
import { ArrowLeft } from "lucide-react";
import { PasswordRequirementsChecklist } from "../components/PasswordRequirementsChecklist";
import {
  describeMissingRequirements,
  validatePassword,
} from "../utils/passwordPolicy";

const SetPassword: React.FC = () => {
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [token, setToken] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const history = useHistory();
  const location = useLocation();
  const { toast } = useToast();

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const tokenParam = urlParams.get("token");
    if (tokenParam) {
      setToken(tokenParam);
    } else {
      toast({
        variant: "destructive",
        title: "Errore",
        description: "Link non valido o incompleto",
      });
    }
  }, [location, toast]);

  const handleSetupPassword = async () => {
    if (!newPassword) {
      toast({
        variant: "destructive",
        title: "Errore",
        description: "Inserisci la nuova password",
      });
      return;
    }
    if (newPassword !== confirmPassword) {
      toast({
        variant: "destructive",
        title: "Errore",
        description: "Le password non coincidono",
      });
      return;
    }
    const policyError = describeMissingRequirements(newPassword);
    if (policyError) {
      toast({
        variant: "destructive",
        title: "Errore",
        description: policyError,
      });
      return;
    }

    setIsLoading(true);
    try {
      const res = await api.post("/setup-password", {
        token,
        new_password: newPassword,
        confirm_password: confirmPassword,
      });

      persistAuthSession(res.data);

      toast({
        variant: "success",
        title: "Password impostata",
        description: "Il tuo account è ora completo.",
      });

      history.push("/loading");
    } catch (e: any) {
      toast({
        variant: "destructive",
        title: "Errore",
        description:
          e?.response?.status === 400
            ? "Link scaduto o non valido"
            : "Errore durante l'impostazione della password",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-5">
      <div className="glass-card w-full max-w-md rounded-2xl p-8 space-y-6 relative">
        <Button
          variant="ghost"
          size="icon"
          className="absolute top-4 left-4 rounded-full"
          onClick={() => history.push("/")}
        >
          <ArrowLeft className="size-6 text-white" />
        </Button>

        <img
          src="./assets/long_light.png"
          alt="logo"
          className="h-12 mx-auto mb-2"
        />

        <h1 className="gradient-text text-2xl font-bold text-center">
          Imposta la tua password
        </h1>
        <p className="text-center text-sm text-white/70">
          Scegli una password personale per completare il tuo account Ecard.
        </p>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="newPassword">Nuova password</Label>
            <LimitedInput
              id="newPassword"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Inserisci la password"
              maxLength={FIELD_LIMITS.password}
              showCounter={false}
              className="backdrop-blur-xl bg-white/5 border border-white/10 shadow-xl"
            />
            <PasswordRequirementsChecklist password={newPassword} />
          </div>

          <div className="space-y-2">
            <Label htmlFor="confirmPassword">Conferma password</Label>
            <LimitedInput
              id="confirmPassword"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Conferma la password"
              maxLength={FIELD_LIMITS.password}
              showCounter={false}
              className="backdrop-blur-xl bg-white/5 border border-white/10 shadow-xl"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4">
          <Button
            size="lg"
            className="w-full bg-gradient-to-r from-teal-200 to-teal-500 text-white hover:opacity-90"
            onClick={handleSetupPassword}
            disabled={
              !token ||
              isLoading ||
              !validatePassword(newPassword).valid ||
              newPassword !== confirmPassword
            }
          >
            {isLoading ? "Salvataggio..." : "Salva password"}
          </Button>

          <Button
            variant="ghost"
            className="w-full text-sm text-teal-400 hover:text-teal-200"
            onClick={() => history.push("/forgot-password")}
          >
            Link scaduto? Recupera accesso
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SetPassword;
